import { DEFAULT_CONFIG } from '@shared/constants';
import { normalizePath } from '@shared/utils/dom';

export interface AnalyticsEvent {
  site_key: string;
  event_id: string;
  event_type: string;
  occurred_at: string;
  visitor_id: string;
  session_key: string;
  page: {
    url: string;
    path: string;
    query: string | null;
    title: string | null;
  };
  referrer: string | null;
  utm: {
    source: string | null;
    medium: string | null;
    campaign: string | null;
    content: string | null;
    term: string | null;
  } | null;
  device: {
    type: string | null;
    os: string | null;
    browser: string | null;
  } | null;
  geo: {
    country: string | null;
  } | null;
  meta: Record<string, any> | null;
}

export class AnalyticsTracker {
  private siteKey: string;
  private visitorId: string;
  private sessionKey: string;
  private platformOrigin: string;
  private eventQueue: AnalyticsEvent[] = [];
  private isProcessing = false;
  private campaignPaths: Set<string> = new Set();

  constructor(
    siteKey: string,
    platformOrigin: string = DEFAULT_CONFIG.platformOrigin,
    campaignPaths: string[] = [],
  ) {
    this.siteKey = siteKey;
    this.platformOrigin = platformOrigin;
    this.visitorId = this.getOrCreateVisitorId();
    this.sessionKey = this.getOrCreateSessionKey();
    this.campaignPaths = new Set(campaignPaths);
  }

  private getOrCreateVisitorId(): string {
    const key = 'campaign_visitor_id';
    let visitorId = localStorage.getItem(key);

    if (!visitorId) {
      visitorId = this.generateId('v');
      localStorage.setItem(key, visitorId);
    }

    return visitorId;
  }

  private getOrCreateSessionKey(): string {
    const key = 'campaign_session_key';
    let sessionKey = sessionStorage.getItem(key);

    if (!sessionKey) {
      sessionKey = this.generateId('s');
      sessionStorage.setItem(key, sessionKey);
    }

    return sessionKey;
  }

  private generateId(prefix: string): string {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
  }

  private shouldTrackPath(path: string): boolean {
    if (this.campaignPaths.size === 0) {
      return false;
    }

    return this.campaignPaths.has(path);
  }

  private getUtmParams(): AnalyticsEvent['utm'] {
    const urlParams = new URLSearchParams(window.location.search);
    const source = urlParams.get('utm_source');
    const medium = urlParams.get('utm_medium');
    const campaign = urlParams.get('utm_campaign');
    const content = urlParams.get('utm_content');
    const term = urlParams.get('utm_term');

    if (!source && !medium && !campaign && !content && !term) {
      return null;
    }

    return { source, medium, campaign, content, term };
  }

  private getDeviceInfo(): AnalyticsEvent['device'] {
    const ua = navigator.userAgent;
    let type = 'desktop';

    if (/mobile/i.test(ua)) {
      type = 'mobile';
    } else if (/tablet|ipad/i.test(ua)) {
      type = 'tablet';
    }

    let browser = 'unknown';
    if (ua.indexOf('Chrome') > -1) browser = 'Chrome';
    else if (ua.indexOf('Safari') > -1) browser = 'Safari';
    else if (ua.indexOf('Firefox') > -1) browser = 'Firefox';
    else if (ua.indexOf('Edge') > -1) browser = 'Edge';

    let os = 'unknown';
    if (ua.indexOf('Win') > -1) os = 'Windows';
    else if (ua.indexOf('Mac') > -1) os = 'macOS';
    else if (ua.indexOf('Linux') > -1) os = 'Linux';
    else if (ua.indexOf('Android') > -1) os = 'Android';
    else if (ua.indexOf('iOS') > -1) os = 'iOS';

    return { type, os, browser };
  }

  trackPageView(): void {
    const currentPath = normalizePath(window.location.pathname);

    if (!this.shouldTrackPath(currentPath)) {
      return;
    }

    const event: AnalyticsEvent = {
      site_key: this.siteKey,
      event_id: this.generateId('evt'),
      event_type: 'page_view',
      occurred_at: new Date().toISOString(),
      visitor_id: this.visitorId,
      session_key: this.sessionKey,
      page: {
        url: window.location.href,
        path: currentPath,
        query: window.location.search || null,
        title: document.title || null,
      },
      referrer: document.referrer || null,
      utm: this.getUtmParams(),
      device: this.getDeviceInfo(),
      geo: { country: null },
      meta: null,
    };

    this.queueEvent(event);
  }

  private queueEvent(event: AnalyticsEvent): void {
    this.eventQueue.push(event);
    this.processQueue();
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.eventQueue.length === 0) {
      return;
    }

    this.isProcessing = true;

    while (this.eventQueue.length > 0) {
      const event = this.eventQueue.shift();
      if (event) {
        await this.sendEvent(event);
      }
    }

    this.isProcessing = false;
  }

  private async sendEvent(event: AnalyticsEvent): Promise<void> {
    try {
      await fetch(`${this.platformOrigin}/api/analytics/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(event),
        keepalive: true,
      });
    } catch (error) {
      console.error('[Analytics] Failed to send event', error);
    }
  }
}
